import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LogoStrip from '@/components/LogoStrip';
import { LANDING } from '@/constants/testIds';
import {
  ArrowRight, ShieldCheck, Brain, Activity, Globe2, Sparkles,
  GitBranch, Radar, Workflow, BarChart3, Network, Layers,
} from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85';

const FEATURES = [
  { icon: Brain, title: 'Deep Reasoning', desc: 'Entity, schema and competitor analysis at PhD-level depth — fully autonomous.' },
  { icon: Workflow, title: 'Agent Orchestration', desc: 'Plans, delegates and prioritizes work across the agentic swarm — 24/7.' },
  { icon: ShieldCheck, title: 'Authority Score™', desc: 'A proprietary 0–100 measure rolling up 17 weighted authority signals.' },
  { icon: Radar, title: 'Competitor Command Center', desc: 'Continuous scraping + ranking shifts, alerted before they hurt.' },
  { icon: Sparkles, title: 'Content Factory', desc: 'FAQs, articles, case studies and press — written, optimized, ready to publish.' },
  { icon: GitBranch, title: 'Authority Graph™', desc: 'Live knowledge graph of your entities, products, leaders and citations.' },
  { icon: BarChart3, title: 'Authority Simulator™', desc: '"What if we publish 25 FAQs?" — model uplift in seconds.' },
  { icon: Network, title: 'Agentic Swarm', desc: '29 specialized agents coordinating research, audit, content, PR and reporting.' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top Nav */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-display">A</span>
            </div>
            <div className="font-display text-lg tracking-tight">AuthorityOS<span className="text-primary">™</span></div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground">Platform</a>
            <a href="#agents" className="text-muted-foreground hover:text-foreground">Agents</a>
            <a href="#how" className="text-muted-foreground hover:text-foreground">How it works</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid={LANDING.loginLink} className="text-sm text-muted-foreground hover:text-foreground">Sign in</Link>
            <Link to="/login">
              <Button data-testid={LANDING.ctaPrimary} size="sm">
                Get started <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div
            className="w-full h-full bg-cover bg-center opacity-[0.08] dark:opacity-[0.12]"
            style={{ backgroundImage: `url(${HERO_BG})` }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 grid-skeleton opacity-30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Newsly.Global presents — Authority Engineering
            </div>
            <h1
              data-testid={LANDING.heroHeadline}
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            >
              The AI Operating System for{' '}
              <span className="text-primary">Authority</span>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              AuthorityOS™ is not SEO. It is an autonomous platform that continuously measures, improves and grows your
              brand&apos;s authority across AI assistants, search engines and knowledge graphs — 24 hours a day.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/login">
                <Button size="lg" data-testid={LANDING.ctaSecondary} className="h-12 px-6 text-base">
                  Run an Authority Assessment <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="h-12 px-6 text-base">
                  Explore the platform
                </Button>
              </a>
            </div>
          </div>

          {/* Hero stat grid (bento) */}
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { v: '0–100', l: 'Authority Score™', sub: '17 weighted signals' },
              { v: '29', l: 'Autonomous agents', sub: 'Orchestrated end-to-end' },
              { v: '15+', l: 'Content formats', sub: 'FAQs to white papers' },
              { v: '24/7', l: 'Continuous loop', sub: 'Research → publish → measure' },
            ].map((s) => (
              <div key={s.l} className="card-hover rounded-xl border border-border bg-card p-6">
                <div className="font-display text-3xl tracking-tight">{s.v}</div>
                <div className="mt-2 text-sm font-medium">{s.l}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo strip */}
      <section className="border-y border-border py-10">
        <div className="max-w-7xl mx-auto px-6">
          <LogoStrip />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24" data-testid={LANDING.featureSection}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Platform</div>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl tracking-tight">
              An elite AI team. <br /> Working around the clock.
            </h2>
            <p className="mt-5 text-muted-foreground text-base">
              AuthorityOS™ delegates work across a swarm of specialized agents — researching, auditing,
              creating, distributing and forecasting authority continuously.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="card-hover rounded-xl border border-border bg-card p-6">
                <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <div className="mt-4 font-medium">{title}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous Loop */}
      <section id="how" className="py-24 bg-secondary/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">The continuous loop</div>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl tracking-tight max-w-3xl">
            Research → Audit → Create → Publish → Measure. Forever.
          </h2>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {['Research', 'Audit', 'Gap Analysis', 'Create', 'Publish', 'Measure', 'Recalculate'].map((step, i) => (
              <div key={step} className="rounded-lg border border-border bg-card p-4">
                <div className="text-xs text-muted-foreground">{String(i + 1).padStart(2, '0')}</div>
                <div className="mt-1 font-medium">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agents section */}
      <section id="agents" className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Agentic swarm</div>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl tracking-tight">
              We lead. We reason and continually decipher. <br />We execute.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Agent Orchestration plans, prioritizes and delegates. Deep Reasoning audits, analyzes and recommends.
              Behind them: 27 more specialized agents.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/login">
                <Button size="lg">Activate AuthorityOS</Button>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              'Discovery', 'Research', 'Entity Graph', 'AEO', 'AI Readiness', 'Competitor Intel',
              'Content Gap', 'FAQ', 'Article', 'Guide', 'Video', 'Podcast', 'Case Study', 'Press',
              'PR', 'Citation', 'Schema', 'Reviews', 'Social', 'Newsletter', 'Email', 'Forecast',
              'Analytics', 'Reporting', 'Publishing', 'Opportunity', 'Certification', 'Revenue',
            ].map(n => (
              <div key={n} className="rounded-md border border-border bg-card p-3 text-xs text-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition">
                {n}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">© {new Date().getFullYear()} AuthorityOS™ — A Newsly.Global product</div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground">Platform</a>
            <a href="#agents" className="hover:text-foreground">Agents</a>
            <Link to="/login" className="hover:text-foreground">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
